import { useMutation, useQuery } from '@tanstack/react-query'
import {
	Button,
	Checkbox,
	EmptyState,
	Modal,
	PdfIcon,
	Search,
	SearchIcon,
	Select,
	Spinner,
	Text,
	ZipIcon,
} from '@viapeople/components'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatStepName } from '../../utils/helpers'
import { useToast } from '../../utils/ToastContext'
import { trpc, trpcClient } from '../../utils/trpc'

/** Fetch a remote URL as a blob and trigger a file download */
const downloadFile = async (url: string, filename: string) => {
	const response = await fetch(url)
	const blob = await response.blob()
	const blobUrl = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = blobUrl
	link.download = filename
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(blobUrl)
}

interface ManagerPdfsTabProps {
	cycleId: string
}

// Types for evaluation data from getManagerEvaluationsForDownload
interface EvaluationTask {
	id: string
	cycleStepDisplayName: string
	evaluatorId: string
	evaluatorFirstName: string | null
	evaluatorLastName: string | null
	calculatedRole: string | null
}

interface Evaluation {
	evaluationId: string
	participantId: string
	firstName: string
	lastName: string
	formId: string | null
	reviewerCount: number
	tasks: EvaluationTask[]
	customFields?: Record<string, string> | null
}

// Step types that have multiple evaluators
const MULTI_EVALUATOR_STEPS = ['group_evaluation', 'unsolicited_evaluation', 'manager_evaluation', 'review'] as const

export const ManagerPdfsTab: React.FC<ManagerPdfsTabProps> = ({ cycleId }) => {
	const { showToast } = useToast()
	const [selectedPhase, setSelectedPhase] = useState<string>('all')
	const [searchQuery, setSearchQuery] = useState<string>('')
	const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set())
	const [currentJobId, setCurrentJobId] = useState<string | null>(null)
	const [isDownloadHistoryModalOpen, setIsDownloadHistoryModalOpen] = useState(false)
	const [singlePdfJobs, setSinglePdfJobs] = useState<Map<string, string>>(new Map()) // taskId → jobId

	// Fetch evaluations using the new endpoint
	const { data: evaluationsData, isLoading } = useQuery({
		...trpc.client.reports.getManagerEvaluationsForDownload.queryOptions({ cycleId }),
		enabled: !!cycleId,
	})

	const evaluations = (evaluationsData?.evaluations || []) as Evaluation[]

	// Fetch job history
	const { data: jobHistory, refetch: refetchHistory } = useQuery({
		...trpc.client.reports.getBulkPdfJobHistory.queryOptions({ cycleId }),
		enabled: !!cycleId,
		refetchInterval: currentJobId ? 5000 : false,
	})

	// Poll for job status when we have a job ID
	const { data: jobStatus } = useQuery({
		...trpc.client.reports.getBulkPdfJobStatus.queryOptions({ jobId: currentJobId as string }),
		enabled: !!currentJobId,
		refetchInterval: 5000,
	})

	const bulkPdfMutation = useMutation({
		...trpc.client.reports.generateBulkPdfAsManager.mutationOptions(),
		onSuccess: (data) => {
			// Show confirmation toast immediately
			showToast(
				`PDF generation started for ${selectedTaskIds.size} ${selectedTaskIds.size === 1 ? 'file' : 'files'}. Check Download History when ready.`,
				'info',
			)
			// Clear selection so user can make new selections
			setSelectedTaskIds(new Set())
			// Track job ID for auto-download when complete
			setCurrentJobId(data.jobId)
			// Refresh history to show the new job
			refetchHistory()
		},
		onError: (error) => {
			showToast(`Failed to start PDF generation: ${error.message}`, 'error')
		},
	})

	// Handle job status changes
	useEffect(() => {
		if (!jobStatus || !currentJobId) return

		if (jobStatus.status === 'completed') {
			setCurrentJobId(null)

			if (jobStatus.downloadUrl) {
				downloadFile(jobStatus.downloadUrl, `bulk-pdfs-${new Date().toISOString().split('T')[0]}.zip`)

				const pdfCount = jobStatus.pdfCount ?? 0
				showToast(`Successfully generated ${pdfCount} PDF${pdfCount !== 1 ? 's' : ''}`)
			} else {
				showToast('PDF generation completed but download URL is missing', 'error')
			}
		} else if (jobStatus.status === 'failed') {
			setCurrentJobId(null)
			showToast(`PDF generation failed: ${jobStatus.errorMessage || 'Unknown error'}`, 'error')
		}
	}, [jobStatus, currentJobId, showToast])

	// Refetch history when modal opens
	useEffect(() => {
		if (isDownloadHistoryModalOpen) {
			refetchHistory()
		}
	}, [isDownloadHistoryModalOpen, refetchHistory])

	// Extract unique phases from evaluations
	const phaseOptions = useMemo(() => {
		if (!evaluations.length) return [{ value: 'all', label: 'All Phases' }]

		const phases = new Set<string>()
		evaluations.forEach((evaluation) => {
			evaluation.tasks.forEach((task) => {
				phases.add(task.cycleStepDisplayName)
			})
		})

		const options = [{ value: 'all', label: 'All Phases' }]
		Array.from(phases)
			.sort()
			.forEach((phase) => {
				options.push({ value: phase, label: formatStepName(phase) })
			})

		return options
	}, [evaluations])

	// Filter evaluations by search and phase
	const filteredEvaluations = useMemo(() => {
		let filtered = evaluations

		// Filter by search
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase()
			filtered = filtered.filter((evaluation) => {
				const fullName = `${evaluation.firstName} ${evaluation.lastName}`.toLowerCase()
				return fullName.includes(query)
			})
		}

		// Filter by phase - only show evaluations that have tasks matching the selected phase
		if (selectedPhase !== 'all') {
			filtered = filtered.filter((evaluation) =>
				evaluation.tasks.some((task) => task.cycleStepDisplayName === selectedPhase),
			)
		}

		// Sort by name
		filtered.sort((a, b) => {
			const nameA = `${a.lastName} ${a.firstName}`.toLowerCase()
			const nameB = `${b.lastName} ${b.firstName}`.toLowerCase()
			return nameA.localeCompare(nameB)
		})

		return filtered
	}, [evaluations, searchQuery, selectedPhase])

	// Calculate total PDF count (sum of all tasks across all evaluations)
	const pdfStats = useMemo(() => {
		const totalParticipants = evaluations.length
		const totalPdfs = evaluations.reduce((sum, e) => sum + e.tasks.length, 0)
		const filteredPdfs = filteredEvaluations.reduce(
			(sum, e) =>
				sum +
				(selectedPhase === 'all'
					? e.tasks.length
					: e.tasks.filter((t) => t.cycleStepDisplayName === selectedPhase).length),
			0,
		)
		const selectedPdfs = selectedTaskIds.size
		return { totalParticipants, totalPdfs, filteredPdfs, selectedPdfs }
	}, [evaluations, filteredEvaluations, selectedPhase, selectedTaskIds])

	// Get tasks for a filtered evaluation (filtered by phase)
	const getFilteredTasks = useCallback(
		(evaluation: Evaluation) => {
			if (selectedPhase === 'all') return evaluation.tasks
			return evaluation.tasks.filter((task) => task.cycleStepDisplayName === selectedPhase)
		},
		[selectedPhase],
	)

	// Format task label (like SearchEvaluation)
	const formatTaskLabel = (task: EvaluationTask) => {
		const stepName = formatStepName(task.cycleStepDisplayName)

		const isMultiEvaluator = (MULTI_EVALUATOR_STEPS as readonly string[]).includes(
			task.cycleStepDisplayName.toLowerCase(),
		)

		if (isMultiEvaluator && task.evaluatorFirstName && task.evaluatorLastName) {
			return `${stepName} - ${task.evaluatorFirstName} ${task.evaluatorLastName}`
		}

		return stepName
	}

	const downloadTaskPdfMutation = useMutation({
		...trpc.client.reports.generateTaskPdfAsManager.mutationOptions(),
	})

	const handleDownload = (taskId: string) => {
		setSinglePdfJobs((prev) => new Map(prev).set(taskId, ''))
		downloadTaskPdfMutation.mutate(
			{ taskId },
			{
				onSuccess: (data) => {
					setSinglePdfJobs((prev) => {
						const next = new Map(prev)
						next.set(taskId, data.jobId)
						return next
					})
				},
				onError: (error) => {
					showToast(`Failed to start PDF generation: ${error.message}`, 'error')
					setSinglePdfJobs((prev) => {
						const next = new Map(prev)
						next.delete(taskId)
						return next
					})
				},
			},
		)
	}

	// Poll for single PDF job completion
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const downloadedJobsRef = useRef<Set<string>>(new Set())
	useEffect(() => {
		const activeJobs = Array.from(singlePdfJobs.entries()).filter(([, jobId]) => jobId !== '')
		if (activeJobs.length === 0) {
			if (pollingRef.current) {
				clearInterval(pollingRef.current)
				pollingRef.current = null
			}
			return
		}

		pollingRef.current = setInterval(async () => {
			for (const [taskId, jobId] of activeJobs) {
				if (downloadedJobsRef.current.has(jobId)) continue
				try {
					const status = await trpcClient.client.reports.getBulkPdfJobStatus.query({ jobId })
					if (status.status === 'completed' && status.downloadUrl) {
						downloadedJobsRef.current.add(jobId)
						downloadFile(status.downloadUrl, `task-pdf-${taskId.slice(-8)}.pdf`)
						setSinglePdfJobs((prev) => {
							const next = new Map(prev)
							next.delete(taskId)
							return next
						})
					} else if (status.status === 'failed') {
						downloadedJobsRef.current.add(jobId)
						showToast(`PDF generation failed: ${status.errorMessage || 'Unknown error'}`, 'error')
						setSinglePdfJobs((prev) => {
							const next = new Map(prev)
							next.delete(taskId)
							return next
						})
					}
				} catch {
					// Silently retry on next interval
				}
			}
		}, 2000)

		return () => {
			if (pollingRef.current) {
				clearInterval(pollingRef.current)
				pollingRef.current = null
			}
		}
	}, [singlePdfJobs, showToast])

	const handleToggleTask = (taskId: string) => {
		setSelectedTaskIds((prev) => {
			const newSet = new Set(prev)
			if (newSet.has(taskId)) {
				newSet.delete(taskId)
			} else {
				newSet.add(taskId)
			}
			return newSet
		})
	}

	const handleToggleEvaluation = (evaluationId: string) => {
		const evaluation = filteredEvaluations.find((e) => e.evaluationId === evaluationId)
		if (!evaluation) return

		const taskIds = getFilteredTasks(evaluation).map((t) => t.id)
		const allSelected = taskIds.every((id) => selectedTaskIds.has(id))

		setSelectedTaskIds((prev) => {
			const newSet = new Set(prev)
			if (allSelected) {
				taskIds.forEach((id) => {
					newSet.delete(id)
				})
			} else {
				taskIds.forEach((id) => {
					newSet.add(id)
				})
			}
			return newSet
		})
	}

	const handleSelectAll = () => {
		const allFilteredTaskIds = filteredEvaluations.flatMap((e) => getFilteredTasks(e).map((t) => t.id))
		const allSelected = allFilteredTaskIds.every((id) => selectedTaskIds.has(id))

		if (allSelected) {
			setSelectedTaskIds(new Set())
		} else {
			setSelectedTaskIds(new Set(allFilteredTaskIds))
		}
	}

	const handleBulkDownload = () => {
		if (selectedTaskIds.size === 0) {
			showToast('Please select at least one task')
			return
		}

		// Pass the selected task IDs directly for granular filtering
		bulkPdfMutation.mutate({
			cycleId,
			format: 'zip',
			taskIds: [...selectedTaskIds],
		})
	}

	if (!cycleId) {
		return (
			<div className='p-6'>
				<EmptyState icon={PdfIcon} title='No Cycle Selected' description='Please select a cycle to manage PDFs' />
			</div>
		)
	}

	if (isLoading) {
		return (
			<div className='p-6 flex justify-center items-center'>
				<Spinner />
			</div>
		)
	}

	return (
		<div className='p-6 space-y-6'>
			{/* Header */}
			<div className='flex items-center justify-between'>
				<div>
					<Text variant='large' weight='semibold'>
						Manage PDFs
					</Text>
					<Text variant='small' className='text-neutral-500 mt-1'>
						Download individual or bulk PDFs for this cycle
					</Text>
				</div>
				{/* Download History Button - always show if there are jobs or currentJobId */}
				{(jobHistory && jobHistory.length > 0) || currentJobId ? (
					<Button
						size='medium'
						variant={currentJobId ? 'primary' : 'secondary'}
						onClick={() => setIsDownloadHistoryModalOpen(true)}
						icon={currentJobId ? undefined : ZipIcon}
						iconPosition='left'>
						{currentJobId ? (
							<>
								<Spinner className='w-4 h-4 mr-2' />
								Processing... (
								{jobHistory?.filter((j) => j.status === 'processing' || j.status === 'pending').length || 1})
							</>
						) : (
							`Download History (${jobHistory?.length || 0})`
						)}
					</Button>
				) : null}
			</div>

			{/* Stats Cards */}
			<div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
				<div className='bg-white border border-neutral-200 rounded-lg p-4 shadow-sm'>
					<Text variant='xsmall' className='text-neutral-500 uppercase tracking-wide'>
						Participants
					</Text>
					<Text variant='h3' weight='semibold' className='text-neutral-900 mt-1'>
						{pdfStats.totalParticipants}
					</Text>
				</div>
				<div className='bg-white border border-neutral-200 rounded-lg p-4 shadow-sm'>
					<Text variant='xsmall' className='text-neutral-500 uppercase tracking-wide'>
						Total PDFs
					</Text>
					<Text variant='h3' weight='semibold' className='text-primary-600 mt-1'>
						{pdfStats.totalPdfs}
					</Text>
				</div>
				<div className='bg-white border border-neutral-200 rounded-lg p-4 shadow-sm'>
					<Text variant='xsmall' className='text-neutral-500 uppercase tracking-wide'>
						Showing
					</Text>
					<Text variant='h3' weight='semibold' className='text-neutral-700 mt-1'>
						{pdfStats.filteredPdfs}
					</Text>
				</div>
				<div className='bg-primary-50 border border-primary-200 rounded-lg p-4 shadow-sm'>
					<Text variant='xsmall' className='text-primary-700 uppercase tracking-wide'>
						Selected
					</Text>
					<Text variant='h3' weight='semibold' className='text-primary-700 mt-1'>
						{pdfStats.selectedPdfs}
					</Text>
				</div>
			</div>

			{/* Search and Filters */}
			<div className='space-y-3'>
				{/* Search Row */}
				<div className='max-w-md'>
					<Search
						placeholder='Search by participant name...'
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						clearable={true}
					/>
				</div>

				{/* Filters Row - wraps on smaller screens */}
				<div className='flex flex-wrap items-end gap-3'>
					{/* Phase Filter */}
					<div className='w-[160px]'>
						<Select
							label='Phase'
							options={phaseOptions}
							value={selectedPhase}
							onChange={(value: string) => setSelectedPhase(value)}
						/>
					</div>

					{/* Clear Filters Button - show when any filter is active */}
					{selectedPhase !== 'all' && (
						<Button
							variant='tertiary'
							size='small'
							onClick={() => {
								setSelectedPhase('all')
							}}>
							Clear Filters
						</Button>
					)}
				</div>

				{/* Bulk Selection Controls */}
				{filteredEvaluations.length > 0 && (
					<div className='flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200'>
						<div className='flex items-center gap-3'>
							<Checkbox
								checked={selectedTaskIds.size > 0}
								isAllSelected={
									selectedTaskIds.size === pdfStats.filteredPdfs ? true : selectedTaskIds.size > 0 ? false : undefined
								}
								onChange={handleSelectAll}
							/>
							<Text variant='small' weight='semibold'>
								{selectedTaskIds.size === 0
									? 'Select PDFs'
									: `${selectedTaskIds.size} PDF${selectedTaskIds.size !== 1 ? 's' : ''} selected`}
							</Text>
						</div>
						<Button
							size='medium'
							variant='primary'
							onClick={handleBulkDownload}
							disabled={selectedTaskIds.size === 0 || bulkPdfMutation.isPending}
							icon={bulkPdfMutation.isPending ? undefined : ZipIcon}
							iconPosition='left'>
							{bulkPdfMutation.isPending ? (
								<>
									<Spinner className='w-4 h-4 mr-2' />
									Starting...
								</>
							) : (
								'Download as ZIP'
							)}
						</Button>
					</div>
				)}
			</div>

			{/* Evaluations List */}
			<div className='space-y-3'>
				{filteredEvaluations.length === 0 ? (
					<div className='py-12'>
						<EmptyState
							icon={SearchIcon}
							title='No Evaluations Found'
							description={
								searchQuery.trim()
									? `No evaluations match "${searchQuery}". Try a different search term.`
									: 'No evaluations match the selected filters.'
							}
						/>
					</div>
				) : (
					filteredEvaluations.map((evaluation) => {
						const filteredTasks = getFilteredTasks(evaluation)
						const selectedCount = filteredTasks.filter((t) => selectedTaskIds.has(t.id)).length
						const allSelected = selectedCount === filteredTasks.length && filteredTasks.length > 0
						const someSelected = selectedCount > 0 && selectedCount < filteredTasks.length
						const initials = `${evaluation.firstName.charAt(0)}${evaluation.lastName.charAt(0)}`.toUpperCase()

						return (
							<div
								key={evaluation.evaluationId}
								className={`border rounded-xl bg-white transition-all duration-200 ${allSelected ? 'ring-2 ring-primary-500 border-primary-300' : someSelected ? 'ring-1 ring-primary-300 border-primary-200' : 'border-neutral-200 hover:border-neutral-300 hover:shadow-sm'}`}>
								{/* Evaluation Header */}
								<button
									type='button'
									className={`flex items-center justify-between p-4 cursor-pointer transition-colors w-full text-left ${allSelected ? 'bg-primary-50/50' : 'hover:bg-neutral-50'}`}
									onClick={() => handleToggleEvaluation(evaluation.evaluationId)}>
									<div className='flex items-center gap-4'>
										{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation wrapper, keyboard events handled by parent button */}
										{/* biome-ignore lint/a11y/noStaticElementInteractions: stopPropagation wrapper for checkbox */}
										<div onClick={(e) => e.stopPropagation()}>
											<Checkbox
												checked={allSelected}
												isAllSelected={someSelected ? false : allSelected ? true : undefined}
												onChange={() => handleToggleEvaluation(evaluation.evaluationId)}
											/>
										</div>
										{/* Avatar */}
										<div
											className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${allSelected ? 'bg-primary-600 text-white' : 'bg-gradient-to-br from-neutral-100 to-neutral-200 text-neutral-600'}`}>
											{initials}
										</div>
										<div>
											<Text variant='medium' weight='semibold' className='text-neutral-900'>
												{evaluation.firstName} {evaluation.lastName}
											</Text>
											<Text variant='xsmall' className='text-neutral-500 mt-0.5'>
												{selectedCount > 0
													? `${selectedCount}/${filteredTasks.length} selected`
													: `${filteredTasks.length} ${filteredTasks.length === 1 ? 'PDF' : 'PDFs'}`}
											</Text>
										</div>
									</div>
								</button>

								{/* Tasks List */}
								<div className='border-t border-neutral-100'>
									{filteredTasks.map((task, index) => {
										const isTaskSelected = selectedTaskIds.has(task.id)

										return (
											<button
												type='button'
												key={task.id}
												className={`flex items-center gap-4 px-4 py-3 transition-colors cursor-pointer w-full text-left ${index !== filteredTasks.length - 1 ? 'border-b border-neutral-50' : ''} ${isTaskSelected ? 'bg-primary-50/30' : 'hover:bg-neutral-50'}`}
												onClick={() => handleToggleTask(task.id)}>
												{/* Checkbox */}
												{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation wrapper, keyboard events handled by parent button */}
												{/* biome-ignore lint/a11y/noStaticElementInteractions: stopPropagation wrapper for checkbox */}
												<div onClick={(e) => e.stopPropagation()} className='flex-shrink-0'>
													<Checkbox checked={isTaskSelected} onChange={() => handleToggleTask(task.id)} />
												</div>

												{/* PDF Icon */}
												<div className='w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0'>
													<PdfIcon className='w-4 h-4 text-blue-600' />
												</div>

												{/* Task Info */}
												<div className='flex-1 min-w-0'>
													<Text variant='small' weight='semibold' className='text-neutral-800'>
														{formatTaskLabel(task)}
													</Text>
													{task.calculatedRole && (
														<Text variant='xsmall' className='text-neutral-500'>
															{formatStepName(task.calculatedRole)}
														</Text>
													)}
												</div>

												{/* Action Button */}
												<Button
													size='small'
													variant='secondary'
													disabled={singlePdfJobs.has(task.id)}
													onClick={(e) => {
														e.stopPropagation()
														handleDownload(task.id)
													}}>
													{singlePdfJobs.has(task.id) ? (
														<>
															<Spinner className='w-3 h-3 mr-1' />
															Generating...
														</>
													) : (
														'Download'
													)}
												</Button>
											</button>
										)
									})}
								</div>
							</div>
						)
					})
				)}
			</div>

			{/* Download History Modal */}
			<Modal
				isOpen={isDownloadHistoryModalOpen}
				onClose={() => setIsDownloadHistoryModalOpen(false)}
				title='Download History'
				className='max-w-2xl'>
				<div className='space-y-4'>
					<Text variant='small' className='text-neutral-500'>
						Your bulk PDF download requests
					</Text>
					{jobHistory && jobHistory.length > 0 ? (
						<div className='space-y-2 max-h-[60vh] overflow-y-auto'>
							{jobHistory.map((job) => (
								<div
									key={job.id}
									className='flex items-center justify-between p-4 bg-white border border-neutral-200 rounded-lg'>
									<div className='flex-1'>
										<div className='flex items-center gap-3'>
											<div>
												{job.status === 'completed' && <div className='w-2 h-2 bg-green-500 rounded-full' />}
												{job.status === 'processing' && <Spinner className='w-4 h-4' />}
												{job.status === 'pending' && <div className='w-2 h-2 bg-yellow-500 rounded-full' />}
												{job.status === 'failed' && <div className='w-2 h-2 bg-red-500 rounded-full' />}
											</div>
											<div>
												<Text variant='small' weight='semibold'>
													{job.status === 'completed' && `${job.pdfCount} PDFs Ready`}
													{job.status === 'processing' && 'Generating PDFs...'}
													{job.status === 'pending' && 'Queued for Processing'}
													{job.status === 'failed' && 'Generation Failed'}
												</Text>
												<Text variant='xsmall' className='text-neutral-500'>
													Requested {new Date(job.createdAt).toLocaleString()}
												</Text>
												{job.errorMessage && (
													<Text variant='xsmall' className='text-red-600 mt-1'>
														{job.errorMessage}
													</Text>
												)}
											</div>
										</div>
									</div>
									<div>
										{job.status === 'completed' && job.downloadUrl && (
											<Button
												size='small'
												variant='primary'
												onClick={() => {
													downloadFile(job.downloadUrl as string, 'bulk-pdfs.zip')
												}}
												icon={ZipIcon}
												iconPosition='left'>
												Download ZIP
											</Button>
										)}
										{job.status === 'processing' && (
											<Text variant='small' className='text-neutral-500'>
												Processing...
											</Text>
										)}
										{job.status === 'pending' && (
											<Text variant='small' className='text-neutral-500'>
												In queue
											</Text>
										)}
									</div>
								</div>
							))}
						</div>
					) : (
						<EmptyState
							icon={ZipIcon}
							title='No Download History'
							description="You haven't requested any bulk PDF downloads yet."
						/>
					)}
				</div>
			</Modal>
		</div>
	)
}
